"use client"

import React from "react"
import { useRouter } from "next/router"

import { AgentBreadcrumbContent } from "@/agents/navigation"
import { SofiaHomeThreadSwitcher } from "@/app-covi"
import { HeaderActionControls } from "@/app-shell/navigation/HeaderActionControls"
import { SIDEBAR_COLLAPSE_BREAKPOINT } from "@/app-shell/navigation/sidebarDimensions"
import { SOFIA_PRODUCT_NAME } from "@/constants/sofia"
import { useCovalOnly } from "@/feature-flags"
import { MetricBreadcrumbContent } from "@/metrics/navigation"
import { ReportBreadcrumbContent } from "@/reports/navigation"
import { RunBreadcrumbContent } from "@/runs/navigation"
import { ScheduledRunBreadcrumbContent } from "@/runs/scheduled"
import { TemplateBreadcrumbContent } from "@/templates/navigation"
import {
  TestSetBreadcrumbActions,
  TestSetBreadcrumbContent,
  TestSetBreadcrumbTitle,
} from "@/test-set/editor"
import { useOrganization } from "@clerk/nextjs"
import {
  Breadcrumb,
  BREADCRUMB_TOOLTIP_CLASS_NAME,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  TruncatedText,
} from "@coval/ui"
import { useMediaQuery } from "usehooks-ts"

import { useBreadcrumbTitle } from "@/contexts/BreadcrumbTitleContext"
import { useHeaderActions } from "@/contexts/HeaderActionsContext"

import {
  buildRichBreadcrumbDescriptors,
  type RichBreadcrumbDescriptor,
} from "./richDefaultPageHeaderBreadcrumbs"

// The open resource's own crumb, which its page may replace with an editable title.
const isRenamableResourceCrumb = (
  descriptor: RichBreadcrumbDescriptor,
): descriptor is Extract<
  RichBreadcrumbDescriptor,
  { type: "metric" | "template" | "scheduled-run" }
> =>
  (descriptor.type === "metric" ||
    descriptor.type === "template" ||
    descriptor.type === "scheduled-run") &&
  descriptor.isLast

interface RichDefaultPageHeaderProps {
  getHref: (baseHref: string) => string
  isDashboardPage: boolean
  dashboardDisplayName?: string
  /** Replaces the dashboard's name crumb on the dashboard itself, where the name is renamed in place. */
  dashboardTitle?: React.ReactNode
  dashboardHeaderActions?: React.ReactNode
}

export const RichDefaultPageHeader = ({
  getHref,
  isDashboardPage,
  dashboardDisplayName,
  dashboardTitle,
  dashboardHeaderActions,
}: RichDefaultPageHeaderProps) => {
  const router = useRouter()
  const { organization } = useOrganization()
  const { headerAction } = useHeaderActions()
  const publishedTitle = useBreadcrumbTitle()
  const responsiveActions =
    headerAction?.type === "custom" && headerAction.responsive
  // Only promise restore when Recently Deleted is actually reachable (Coval-only).
  const recentlyDeletedEnabled = useCovalOnly()
  // When primary navigation moves into the mobile menu, the breadcrumb carries Sofia's thread history.
  const isCompactSidebar = useMediaQuery(
    `(max-width: ${SIDEBAR_COLLAPSE_BREAKPOINT}px)`,
  )

  // Only the current crumb is the page's heading; an unlinked crumb earlier in
  // the trail is plain text.
  const renderLinkOrPage = (descriptor: {
    href?: string
    children: React.ReactNode
    isCurrent: boolean
  }) => {
    return (
      <TruncatedText
        asChild
        tooltipClassName={BREADCRUMB_TOOLTIP_CLASS_NAME}
        tabIndex={descriptor.href ? undefined : 0}
      >
        {descriptor.href ? (
          <BreadcrumbLink href={descriptor.href}>
            {descriptor.children}
          </BreadcrumbLink>
        ) : descriptor.isCurrent ? (
          <BreadcrumbPage>{descriptor.children}</BreadcrumbPage>
        ) : (
          <span>{descriptor.children}</span>
        )}
      </TruncatedText>
    )
  }

  const renderBreadcrumbDescriptor = (
    descriptor: RichBreadcrumbDescriptor,
    isLast: boolean,
  ): React.ReactNode => {
    if (
      publishedTitle &&
      isRenamableResourceCrumb(descriptor) &&
      descriptor.id === publishedTitle.resourceId
    ) {
      return publishedTitle.title
    }
    switch (descriptor.type) {
      case "label":
        return renderLinkOrPage({
          isCurrent: isLast,
          href: descriptor.href,
          children: descriptor.label,
        })
      case "dashboard-name":
        if (!descriptor.href && dashboardTitle) {
          return dashboardTitle
        }
        return renderLinkOrPage({
          isCurrent: isLast,
          href: descriptor.href,
          children: descriptor.label,
        })
      case "sofia-thread-switcher":
        return <SofiaHomeThreadSwitcher />
      case "sofia-brand":
        // Home's switcher crumb is a control, not a page, so the brand stays
        // the page heading after it.
        return renderLinkOrPage({
          children: SOFIA_PRODUCT_NAME,
          isCurrent: true,
        })
      case "run-title":
        return renderLinkOrPage({
          isCurrent: isLast,
          href: descriptor.href,
          children: <RunBreadcrumbContent runId={descriptor.runId} />,
        })
      case "test-set":
        // The current test set's crumb is its editable title, kept out of the link wrapper so the button and field are not nested in a link.
        if (descriptor.isLast) {
          return (
            <TestSetBreadcrumbTitle
              key={descriptor.id}
              segment={descriptor.id}
            />
          )
        }
        return renderLinkOrPage({
          isCurrent: isLast,
          href: descriptor.href,
          children: (
            <TestSetBreadcrumbContent
              segment={descriptor.id}
              isLast={descriptor.isLast}
            />
          ),
        })
      case "agent":
        return renderLinkOrPage({
          isCurrent: isLast,
          href: descriptor.href,
          children: (
            <AgentBreadcrumbContent
              agentId={descriptor.id}
              isLast={descriptor.isLast}
            />
          ),
        })
      case "metric":
        return renderLinkOrPage({
          isCurrent: isLast,
          href: descriptor.href,
          children: (
            <MetricBreadcrumbContent
              metricId={descriptor.id}
              isLast={descriptor.isLast}
            />
          ),
        })
      case "report":
        return renderLinkOrPage({
          isCurrent: isLast,
          href: descriptor.href,
          children: (
            <ReportBreadcrumbContent
              reportId={descriptor.id}
              isLast={descriptor.isLast}
            />
          ),
        })
      case "template":
        return renderLinkOrPage({
          isCurrent: isLast,
          href: descriptor.href,
          children: (
            <TemplateBreadcrumbContent
              templateId={descriptor.id}
              isLast={descriptor.isLast}
            />
          ),
        })
      case "scheduled-run":
        return renderLinkOrPage({
          isCurrent: isLast,
          href: descriptor.href,
          children: (
            <ScheduledRunBreadcrumbContent
              scheduledRunId={descriptor.id}
              isLast={descriptor.isLast}
            />
          ),
        })
    }
  }

  // Sofia Home's chat switcher is the fallback form of the sidebar's own Sofia
  // rows: the sidebar carries the history where it has the width, this crumb
  // where it does not. Filtering the descriptor out here rather than inside the
  // switcher keeps the separator chevrons counted off what actually renders.
  const breadcrumbDescriptors = buildRichBreadcrumbDescriptors({
    pathname: router.asPath,
    organizationSlug: organization?.slug ?? undefined,
    isDashboardPage,
    dashboardDisplayName,
    getHref,
  }).filter(
    (descriptor) =>
      descriptor.type !== "sofia-thread-switcher" || isCompactSidebar,
  )

  return (
    <div className="flex flex-1 min-w-0 items-center justify-between gap-2">
      <div
        className={`flex justify-start gap-2 items-center min-w-0 ${responsiveActions ? "flex-[0_1_auto]" : "flex-1"}`}
      >
        <Breadcrumb className="min-w-0 max-w-full">
          <BreadcrumbList className="flex-nowrap whitespace-nowrap">
            {breadcrumbDescriptors.map((descriptor, i) => (
              <React.Fragment key={descriptor.key}>
                {i > 0 && <BreadcrumbSeparator className="shrink-0" />}
                <BreadcrumbItem className="min-w-0">
                  {renderBreadcrumbDescriptor(
                    descriptor,
                    i === breadcrumbDescriptors.length - 1,
                  )}
                </BreadcrumbItem>
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div
        className={`flex min-w-0 items-center gap-2 ${responsiveActions ? "flex-1" : "shrink-0"}`}
      >
        {isDashboardPage && dashboardHeaderActions}
        <HeaderActionControls headerAction={headerAction} />
        <TestSetBreadcrumbActions
          recentlyDeletedEnabled={recentlyDeletedEnabled}
        />
      </div>
    </div>
  )
}
